import { useState, useEffect, useMemo } from "react";
import { useDeepLinkTarget, clearPendingDeepLink } from "../utils/deepLink"; // Block D20/D23: notification item deep-link
import { useTranslation } from "react-i18next";
import { useContacts } from "../contexts/ContactsContext";
import { useInvoices } from "../contexts/InvoicesContext";
import { useExpenses } from "../contexts/ExpensesContext";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import { api } from "../api/client";
import ConfirmDeleteModal from './shared/ConfirmDeleteModal';
import { getProjects, createProject } from "../services/projectService";

// ─── Constants ────────────────────────────────────────────────────────────────
const CURRENCIES = [
  { code: "GBP", symbol: "£",    name: "British Pound" },
  { code: "USD", symbol: "$",    name: "US Dollar" },
  { code: "EUR", symbol: "€",    name: "Euro" },
  { code: "CAD", symbol: "CA$",  name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$",   name: "Australian Dollar" },
  { code: "CHF", symbol: "CHF",  name: "Swiss Franc" },
  { code: "JPY", symbol: "¥",    name: "Japanese Yen" },
  { code: "SEK", symbol: "kr",   name: "Swedish Krona" },
  { code: "NOK", symbol: "kr",   name: "Norwegian Krone" },
  { code: "DKK", symbol: "kr",   name: "Danish Krone" },
  { code: "NZD", symbol: "NZ$",  name: "New Zealand Dollar" },
  { code: "SGD", symbol: "S$",   name: "Singapore Dollar" },
  { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
  { code: "ZAR", symbol: "R",    name: "South African Rand" },
];

const RECUR_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

const STATUS = {
  paid: { bg: "#eaf6ef", text: "#1e6645", border: "#6abf85", label: "Paid" },
  pending: { bg: "#fff8ee", text: "#7a4a10", border: "#FBC490", label: "Pending" },
  overdue: { bg: "#fde8e8", text: "#A12710", border: "#f5a5a5", label: "Overdue" },
  draft: { bg: "#f0f0f0", text: "#888", border: "#ccc", label: "Draft" },
  cancelled: { bg: "#f0f0f0", text: "#999", border: "#ccc", label: "Cancelled" },
};

const TABS = ["All", "Pending", "Paid", "Overdue", "Recurring", "Drafts"];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getNextNumber(invoices) {
  const year = new Date().getFullYear();
  const prefix = `INV-${year}-`;
  const max = invoices.reduce((m, i) => {
    if (!i.number?.startsWith(prefix)) return m;
    return Math.max(m, parseInt(i.number.slice(prefix.length)) || 0);
  }, 0);
  return `${prefix}${String(max + 1).padStart(4, "0")}`;
}

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function addFrequency(dateStr, freq) {
  const d = new Date(dateStr);
  if (freq === "weekly") d.setDate(d.getDate() + 7);
  if (freq === "monthly") d.setMonth(d.getMonth() + 1);
  if (freq === "quarterly") d.setMonth(d.getMonth() + 3);
  if (freq === "annually") d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

function calcTotals(lineItems) {
  const subtotal = lineItems.reduce((s, r) => s + ((Number(r.qty) || 0) * (Number(r.unitPrice) || 0)), 0);
  const tax = lineItems.reduce((s, r) => s + ((Number(r.qty) || 0) * (Number(r.unitPrice) || 0) * (Number(r.tax) || 0) / 100), 0);
  return { subtotal, tax, total: subtotal + tax };
}

function fmtMoney(amount, symbol) {
  return `${symbol}${amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function currencySymbol(code) {
  return CURRENCIES.find(c => c.code === code)?.symbol || "£";
}

function emptyLine() {
  return { id: Date.now() + Math.random(), description: "", qty: 1, unitPrice: 0, tax: 0 };
}

const REMINDER_PRESETS = [
  { daysOffset: -3, label: "3 days before due" },
  { daysOffset: -1, label: "1 day before due" },
  { daysOffset: 0, label: "On due date" },
  { daysOffset: 3, label: "3 days after due" },
  { daysOffset: 7, label: "7 days after due" },
  { daysOffset: 14, label: "14 days after due" },
];

// Write a payment-chase task via the API and return a promise resolving to its id
async function createChaseTask(inv) {
  const sym = currencySymbol(inv.currency);
  const { total } = calcTotals(inv.lineItems);
  const start = new Date(`${inv.dueDate}T09:00:00`);
  const end = new Date(start.getTime() + 3600000);
  const task = {
    title: `Chase payment — ${inv.number} (${inv.contactName})`,
    datetime: start.toISOString(),
    deadline: end.toISOString(),
    taskType: "Invoices",
    priority: "Medium",
    details: `Invoice ${inv.number} for ${fmtMoney(total, sym)} is due. Check whether payment has been received from ${inv.contactName} and mark as paid.`,
    completed: false,
    assignedTeam: [],
    busy: false,
    invoiceId: inv.id,
  };
  const res = await api('/api/tasks', { method: 'POST', body: JSON.stringify(task) });
  const data = await res.json();
  return data.task?.id ?? Date.now();
}

// Mark the linked chase task as completed
function completeLinkedTask(taskId) {
  if (!taskId) return;
  api(`/api/tasks/${taskId}`, { method: 'PUT', body: JSON.stringify({ completed: true }) })
    .catch(e => console.warn("Could not complete chase task:", e));
}

// Calculate the ISO date for a reminder given invoice dueDate + offset
function reminderDate(dueDate, daysOffset) {
  return addDays(dueDate, daysOffset);
}

const today = new Date().toISOString().slice(0, 10);

// ─── PDF export ───────────────────────────────────────────────────────────────
function printInvoice(inv, paymentDetails = '') {
  const sym = currencySymbol(inv.currency);
  const totals = calcTotals(inv.lineItems);
  const rows = inv.lineItems.map(r => {
    const line = r.qty * r.unitPrice;
    const tax = line * r.tax / 100;
    return `<tr>
      <td>${r.description}</td>
      <td style="text-align:center">${r.qty}</td>
      <td style="text-align:right">${fmtMoney(r.unitPrice, sym)}</td>
      <td style="text-align:center">${r.tax}%</td>
      <td style="text-align:right">${fmtMoney(line + tax, sym)}</td>
    </tr>`;
  }).join("");

  const watermark = inv.status === "paid"
    ? `<div style="position:fixed;top:40%;left:50%;transform:translate(-50%,-50%) rotate(-30deg);font-size:80px;font-weight:900;color:rgba(30,102,69,0.08);pointer-events:none;white-space:nowrap;z-index:0">PAID</div>`
    : inv.status === "overdue"
      ? `<div style="position:fixed;top:40%;left:50%;transform:translate(-50%,-50%) rotate(-30deg);font-size:80px;font-weight:900;color:rgba(161,39,16,0.08);pointer-events:none;white-space:nowrap;z-index:0">OVERDUE</div>`
      : "";

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
  <title>Invoice ${inv.number}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,sans-serif;color:#333;padding:48px;font-size:14px;line-height:1.5}
    .top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:40px}
    .brand{font-size:26px;font-weight:900;color:#0C2D48;letter-spacing:-0.5px}
    .brand span{color:#E4E69B}
    .inv-meta{text-align:right}
    .inv-meta h2{font-size:22px;color:#0C2D48;margin-bottom:4px}
    .inv-meta p{font-size:12px;color:#666;margin-top:2px}
    .status-badge{display:inline-block;padding:3px 10px;border-radius:8px;font-size:11px;font-weight:700;margin-top:6px;
      background:${STATUS[inv.status]?.bg};color:${STATUS[inv.status]?.text};border:1px solid ${STATUS[inv.status]?.border}}
    .divider{border:none;border-top:2px solid #0C2D48;margin:24px 0}
    .bill-section{display:flex;gap:60px;margin-bottom:32px}
    .bill-section h4{font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:#aaa;margin-bottom:8px}
    .bill-section p{font-size:13px;color:#333}
    table{width:100%;border-collapse:collapse;margin-bottom:24px}
    th{background:#0C2D48;color:#fff;padding:10px 12px;text-align:left;font-size:12px;font-weight:700}
    td{padding:10px 12px;border-bottom:1px solid #f0f0f0;font-size:13px}
    tr:last-child td{border-bottom:none}
    .totals{margin-left:auto;width:280px}
    .totals tr td{padding:6px 12px;font-size:13px}
    .totals tr td:last-child{text-align:right;font-weight:600}
    .totals .grand-total td{font-size:16px;font-weight:900;color:#0C2D48;border-top:2px solid #0C2D48;padding-top:10px}
    .notes{margin-top:32px;padding:16px;background:#f9f9f9;border-radius:8px;font-size:12px;color:#666}
    .notes strong{display:block;margin-bottom:4px;color:#333}
    .footer{margin-top:48px;text-align:center;font-size:11px;color:#aaa;border-top:1px solid #eee;padding-top:16px}
    @media print{@page{size:A4;margin:0}body{padding:32px}.no-print{display:none}}
  </style>
  </head><body>
  ${watermark}
  <div style="position:relative;z-index:1">
  <div class="top">
    <div class="brand">Aide<span>y</span></div>
    <div class="inv-meta">
      <h2>${inv.number}</h2>
      <p>Issued: ${fmtDate(inv.issueDate)}</p>
      <p>Due: ${fmtDate(inv.dueDate)}</p>
      ${inv.paidAt ? `<p>Paid: ${fmtDate(inv.paidAt)}</p>` : ""}
      <span class="status-badge">${STATUS[inv.status]?.label}</span>
    </div>
  </div>
  <hr class="divider"/>
  <div class="bill-section">
    <div><h4>Bill to</h4>
      <p><strong>${inv.contactName}</strong></p>
      ${inv.contactCompany ? `<p>${inv.contactCompany}</p>` : ""}
      ${inv.contactAddress ? `<p>${inv.contactAddress}</p>` : ""}
      ${inv.contactEmail ? `<p>${inv.contactEmail}</p>` : ""}
    </div>
    ${inv.recurring ? `<div><h4>Recurring</h4><p style="text-transform:capitalize">${inv.recurring.frequency}</p><p>Next: ${fmtDate(inv.recurring.nextDate)}</p></div>` : ""}
  </div>
  <table>
    <thead><tr>
      <th style="width:45%">Description</th>
      <th style="width:8%;text-align:center">Qty</th>
      <th style="width:17%;text-align:right">Unit Price</th>
      <th style="width:10%;text-align:center">Tax</th>
      <th style="width:17%;text-align:right">Amount</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <table class="totals">
    <tr><td>Subtotal</td><td>${fmtMoney(totals.subtotal, sym)}</td></tr>
    <tr><td>Tax</td><td>${fmtMoney(totals.tax, sym)}</td></tr>
    <tr class="grand-total"><td>Total (${inv.currency})</td><td>${fmtMoney(totals.total, sym)}</td></tr>
  </table>
  ${inv.notes ? `<div class="notes"><strong>Notes</strong>${inv.notes}</div>` : ""}
  ${paymentDetails ? `<div class="notes" style="margin-top:16px"><strong>Payment Details</strong><br/>${paymentDetails.replace(/\n/g, '<br/>')}</div>` : ""}
  <div class="footer">Generated by Aidey · ${new Date().toLocaleDateString("en-GB")}</div>
  <div class="no-print" style="margin-top:24px;text-align:center">
    <button onclick="window.print()" style="padding:10px 24px;background:#0C2D48;color:#fff;border:none;border-radius:6px;font-size:14px;font-weight:700;cursor:pointer">
      Print / Save as PDF
    </button>
  </div>
  </div></body></html>`;

  const win = window.open("", "_blank");
  if (!win) { alert("Allow pop-ups to export invoices as PDF."); return; }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 600);
}

// ─── Line items editor ────────────────────────────────────────────────────────
function LineItemsEditor({ items, onChange, sym }) {
  const isMobileLayout = typeof window !== 'undefined'
    && (window.innerWidth <= 1024 || window.matchMedia('(pointer: coarse)').matches);
  function update(id, field, val) {
    onChange(items.map(r => r.id === id ? { ...r, [field]: field === "description" ? val : parseFloat(val) || 0 } : r));
  }
  function remove(id) { onChange(items.filter(r => r.id !== id)); }
  function add() { onChange([...items, emptyLine()]); }

  const { subtotal, tax, total } = calcTotals(items);

  return (
    <div>
      <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", minWidth: 320 }}>
          <thead>
            <tr style={{ background: "#f8f9fa" }}>
              <th style={{ padding: "0.5rem 0.6rem", textAlign: "left", fontWeight: 700, color: "#555", borderBottom: "2px solid #e5e7eb" }}>Description</th>
              <th style={{ padding: "0.5rem 0.6rem", textAlign: "center", fontWeight: 700, color: "#555", borderBottom: "2px solid #e5e7eb", width: 56, whiteSpace: "nowrap" }}>Qty</th>
              <th style={{ padding: "0.5rem 0.6rem", textAlign: "right", fontWeight: 700, color: "#555", borderBottom: "2px solid #e5e7eb", width: 100, whiteSpace: "nowrap" }}>Unit price</th>
              <th style={{ padding: "0.5rem 0.6rem", textAlign: "center", fontWeight: 700, color: "#555", borderBottom: "2px solid #e5e7eb", width: 64, whiteSpace: "nowrap" }}>Tax %</th>
              <th style={{ padding: "0.5rem 0.6rem", textAlign: "right", fontWeight: 700, color: "#555", borderBottom: "2px solid #e5e7eb", width: 88, whiteSpace: "nowrap" }}>Total</th>
              <th style={{ padding: "0.5rem 0.3rem", borderBottom: "2px solid #e5e7eb", width: 28 }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r, idx) => {
              const lineTotal = r.qty * r.unitPrice * (1 + r.tax / 100);
              return (
                <tr key={r.id} style={{ background: idx % 2 === 1 ? "#fafafa" : "#fff" }}>
                  <td style={{ padding: "0.35rem 0.5rem" }}>
                    <input value={r.description} onChange={e => update(r.id, "description", e.target.value)}
                      placeholder="Item description" style={{ ...cellInput, width: "100%" }} />
                  </td>
                  <td style={{ padding: "0.35rem 0.4rem" }}>
                    <input type="number" min="1" value={r.qty} onChange={e => update(r.id, "qty", e.target.value)}
                      style={{ ...cellInput, textAlign: "center", width: "100%" }} />
                  </td>
                  <td style={{ padding: "0.35rem 0.4rem" }}>
                    <input type="number" min="0" step="0.01" value={r.unitPrice} onChange={e => update(r.id, "unitPrice", e.target.value)}
                      style={{ ...cellInput, textAlign: "right", width: "100%" }} />
                  </td>
                  <td style={{ padding: "0.35rem 0.4rem" }}>
                    <input type="number" min="0" max="100" value={r.tax} onChange={e => update(r.id, "tax", e.target.value)}
                      style={{ ...cellInput, textAlign: "center", width: "100%" }} />
                  </td>
                  <td style={{ padding: "0.35rem 0.6rem", textAlign: "right", fontWeight: 600, whiteSpace: "nowrap", color: "#0C2D48" }}>
                    {fmtMoney(lineTotal, sym)}
                  </td>
                  <td style={{ padding: "0.35rem 0.2rem", textAlign: "center" }}>
                    {items.length > 1 && (
                      <button onClick={() => remove(r.id)} style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: "0.9rem", padding: "0 4px" }}>✕</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <button onClick={add} style={{ marginTop: "0.5rem", background: "none", border: "1px dashed #ccc", borderRadius: 6, padding: "0.35rem 0.9rem", cursor: "pointer", fontSize: "0.82rem", color: "#888", fontFamily: "Raleway, sans-serif" }}>
        + Add line item
      </button>

      <div style={{ marginTop: "0.85rem", marginLeft: "auto", width: isMobileLayout ? "100%" : 240, fontSize: "0.85rem" }}>
        {[["Subtotal", subtotal], ["Tax", tax]].map(([label, val]) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "0.25rem 0", color: "#666", borderBottom: "1px solid #f0f0f0" }}>
            <span>{label}</span><span>{fmtMoney(val, sym)}</span>
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "space-between", padding: "0.5rem 0 0.2rem", fontWeight: 800, fontSize: "1rem", color: "#0C2D48", marginTop: "0.2rem" }}>
          <span>Total</span><span>{fmtMoney(total, sym)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Invoice form modal ───────────────────────────────────────────────────────
function InvoiceFormModal({ invoice, allInvoices, onSave, onClose, preselectedProjectId, preselectedTimeEntries }) {
  const isMobileLayout = typeof window !== 'undefined'
    && (window.innerWidth <= 1024 || window.matchMedia('(pointer: coarse)').matches);
  const { contacts } = useContacts();
  const { expenses: allExpenses } = useExpenses();
  const isEdit = !!invoice;

  const [number, setNumber] = useState(invoice?.number ?? getNextNumber(allInvoices));
  const [contactId, setContactId] = useState(invoice?.contactId ?? "__manual__");
  const [contactName, setContactName] = useState(invoice?.contactName ?? "");
  const [contactEmail, setContactEmail] = useState(invoice?.contactEmail ?? "");
  const [contactCo, setContactCo] = useState(invoice?.contactCompany ?? "");
  const [contactAddr, setContactAddr] = useState(invoice?.contactAddress ?? "");
  const [issueDate, setIssueDate] = useState(invoice?.issueDate ?? today);
  const [dueDate, setDueDate] = useState(invoice?.dueDate ?? addDays(today, 30));
  const [currency, setCurrency] = useState(invoice?.currency ?? "GBP");
  const [notes, setNotes] = useState(invoice?.notes ?? "");
  const [isRecurring, setIsRecurring] = useState(!!(invoice?.recurring));
  const [recurFreq, setRecurFreq] = useState(invoice?.recurring?.frequency ?? "monthly");
  const [lineItems, setLineItems] = useState(invoice?.lineItems ?? [emptyLine()]);
  const [createTask, setCreateTask] = useState(!isEdit);
  const [invoiceStatus, setInvoiceStatus] = useState(invoice?.status ?? "pending");
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(invoice?.project_id ?? preselectedProjectId ?? null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [poNumber, setPoNumber] = useState(invoice?.poNumber ?? "");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  // Billable expenses
  const unbilledBillable = allExpenses.filter(e => e.is_billable && !e.is_billed);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [expProjectFilter, setExpProjectFilter] = useState("");

  // Billable time
  const [billableTimeData, setBillableTimeData] = useState(null);   // { hourlyRate, projects: [...] }
  const [billableTimeLoading, setBillableTimeLoading] = useState(false);
  const [showBillableTime, setShowBillableTime] = useState(false);
  const [selectedTimeIds, setSelectedTimeIds] = useState(new Set());

  // Pre-populate from "Bill selected" flow in TimesheetView
  useEffect(() => {
    if (!preselectedTimeEntries?.length) return;
    const grouped = new Map();
    for (const e of preselectedTimeEntries) {
      const pid = e.projectId || 'none';
      if (!grouped.has(pid)) {
        grouped.set(pid, {
          projectId: e.projectId || null,
          projectName: e.projectName || 'Time tracking',
          totalMinutes: 0,
          totalHours: 0,
          suggestedAmount: null,
          entries: [],
        });
      }
      const g = grouped.get(pid);
      g.totalMinutes += e.duration;
      g.entries.push(e);
    }
    [...grouped.values()].forEach(g => { g.totalHours = Math.round((g.totalMinutes / 60) * 100) / 100; });
    setBillableTimeData({ hourlyRate: null, projects: [...grouped.values()] });
    setSelectedTimeIds(new Set(preselectedTimeEntries.map(e => e.id)));
    setShowBillableTime(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch billable time when a CRM contact is selected
  useEffect(() => {
    if (!contactId || contactId === '__manual__' || preselectedTimeEntries?.length) return;
    setBillableTimeData(null);
    setBillableTimeLoading(true);
    api(`/api/invoices/billable-time/${contactId}`)
      .then(r => r.json())
      .then(data => {
        if (data.projects?.length) {
          setBillableTimeData(data);
        } else {
          setBillableTimeData(null);
        }
      })
      .catch(() => setBillableTimeData(null))
      .finally(() => setBillableTimeLoading(false));
  }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleTimeId(id) {
    setSelectedTimeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function selectAllTime() {
    const all = billableTimeData?.projects.flatMap(p => p.entries.map(e => e.id)) || [];
    setSelectedTimeIds(new Set(all));
  }

  function deselectAllTime() { setSelectedTimeIds(new Set()); }

  const sym = currencySymbol(currency);

  function pickContact(id) {
    setContactId(id);
    if (id === "__manual__") return;
    const c = contacts.find(c => String(c.id) === id);
    if (c) {
      setContactName(c.name);
      setContactEmail(c.email || "");
      setContactCo(c.company || "");
      setContactAddr(c.address || "");
      if (c.defaultCurrency) setCurrency(c.defaultCurrency);
    }
  }

  useEffect(() => {
    let alive = true;
    const loadProjects = async () => {
      try {
        const items = await getProjects();
        if (alive) setProjects(Array.isArray(items) ? items : []);
      } catch {
        if (alive) setProjects([]);
      }
    };

    loadProjects();

    const onProjectsUpdated = () => {
      loadProjects();
    };
    window.addEventListener('aidey:projectsUpdated', onProjectsUpdated);

    return () => {
      alive = false;
      window.removeEventListener('aidey:projectsUpdated', onProjectsUpdated);
    };
  }, []);

  async function handleCreateProjectInline() {
    if (!newProjectName.trim()) return;

    try {
      const p = await createProject(newProjectName.trim());
      setProjectId(p.id);
      setProjects(prev => {
        if (prev.some(x => x.id === p.id)) return prev;
        return [...prev, p];
      });
      setNewProjectName("");
      setShowNewProject(false);
    } catch (err) {
      console.error('Failed to create project:', err);
    }
  }

  const hasValidLineItems = lineItems.some(r => r.description.trim()) || selectedExpenses.length > 0;
  const canSubmit = contactName.trim() && hasValidLineItems && !saving;

  async function handleSave() {
    if (!contactName.trim()) {
      setFormError("Please enter a contact name.");
      return;
    }
    if (!hasValidLineItems) {
      setFormError("Add at least one line item with a description before saving.");
      return;
    }
    setFormError("");
    setSaving(true);
    const rec = isRecurring
      ? { frequency: recurFreq, nextDate: addFrequency(issueDate, recurFreq) }
      : null;
    // Convert selected billable expenses to line items
    const expenseLines = selectedExpenses.map(eid => {
      const exp = unbilledBillable.find(e => e.id === eid);
      if (!exp) return null;
      return { id: Date.now() + Math.random(), description: `${exp.title} (expense)`, qty: 1, unitPrice: Number(exp.amount), tax: Number(exp.tax_rate) || 0 };
    }).filter(Boolean);

    // Convert selected billable time entries to line items
    const allTimeEntries = billableTimeData?.projects.flatMap(p => p.entries) || [];
    const selectedTimeEntries = allTimeEntries.filter(e => selectedTimeIds.has(e.id));
    const timeLines = selectedTimeEntries.map(e => ({
      id: Date.now() + Math.random(),
      description: `${e.projectName || 'Time tracking'}${e.note ? ` — ${e.note}` : ''}`,
      qty: Math.round((e.duration / 60) * 100) / 100,
      unitPrice: billableTimeData?.hourlyRate || 0,
      tax: 0,
    }));

    try {
      await onSave({
        id: invoice?.id ?? Date.now(),
        number,
        contactId: contactId === "__manual__" ? null : contactId,
        contactName: contactName.trim(),
        contactEmail: contactEmail.trim(),
        contactCompany: contactCo.trim(),
        contactAddress: contactAddr.trim(),
        issueDate,
        dueDate,
        status: invoiceStatus,
        paidAt: invoice?.paidAt ?? null,
        currency,
        notes: notes.trim(),
        recurring: rec,
        lineItems: [...lineItems.filter(r => r.description.trim() || r.unitPrice > 0), ...expenseLines, ...timeLines],
        createdAt: invoice?.createdAt ?? new Date().toISOString(),
        linkedTaskId: invoice?.linkedTaskId ?? null,
        reminders: invoice?.reminders ?? [],
        project_id: projectId,
        poNumber: poNumber.trim() || null,
        _selectedExpenseIds: selectedExpenses,
        _selectedTimeIds: [...selectedTimeIds],
        _createTask: createTask && !isEdit,
      });
    } finally {
      // Only runs if the modal wasn't unmounted (i.e. save failed and modal stayed open)
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content neutral" style={{ maxWidth: 740, width: "95vw", maxHeight: "90vh", overflowY: "auto", overflowX: "hidden", boxSizing: "border-box" }}>
        <button className="close-button" onClick={onClose}>✕</button>
        <h3 style={{ marginBottom: "1.25rem" }}>{isEdit ? "Edit Invoice" : "New Invoice"}</h3>

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {/* Top row */}
          <div style={{ display: "grid", gridTemplateColumns: isMobileLayout ? "1fr" : "1fr 1fr 140px", gap: "0.75rem" }}>
            <label style={lbl}>
              Invoice number
              <input value={number} onChange={e => setNumber(e.target.value)} style={inp} />
            </label>
            <label style={lbl}>
              Currency
              <select value={currency} onChange={e => setCurrency(e.target.value)} style={{ ...inp, fontFamily: "Raleway, sans-serif" }}>
                {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name} ({c.symbol})</option>)}
              </select>
            </label>
            <label style={lbl}>
              Status
              <select
                value={invoiceStatus}
                onChange={e => setInvoiceStatus(e.target.value)}
                disabled={isEdit && invoice?.status !== "draft"}
                style={{ ...inp, fontFamily: "Raleway, sans-serif", background: (isEdit && invoice?.status !== "draft") ? "#f8f8f8" : "#fff" }}
              >
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            </label>
          </div>

          {/* Contact */}
          <div>
            <div style={{ ...lbl, marginBottom: "0.4rem" }}>Contact</div>
            {contacts.length > 0 && (
              <select value={contactId} onChange={e => pickContact(e.target.value)}
                style={{ ...inp, marginBottom: "0.5rem", fontFamily: "Raleway, sans-serif" }}>
                <option value="__manual__">— Enter manually —</option>
                {contacts.map(c => (
                  <option key={c.id} value={String(c.id)}>{c.name}{c.company ? ` (${c.company})` : ""}</option>
                ))}
              </select>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isMobileLayout ? "1fr" : "1fr 1fr", gap: "0.65rem" }}>
              <label style={lbl}>Name <span style={{ color: "#E96A6A" }}>*</span>
                <input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Full name" style={inp} />
              </label>
              <label style={lbl}>Email
                <input value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="email@…" style={inp} />
              </label>
              <label style={{ ...lbl, gridColumn: "1 / -1" }}>Company
                <input value={contactCo} onChange={e => setContactCo(e.target.value)} placeholder="Company name" style={inp} />
              </label>
              <label style={{ ...lbl, gridColumn: "1 / -1" }}>Address
                <input value={contactAddr} onChange={e => setContactAddr(e.target.value)} placeholder="Street, City, Postcode" style={inp} />
              </label>
            </div>
          </div>

          {/* Dates */}
          <div style={{ display: "grid", gridTemplateColumns: isMobileLayout ? "1fr" : "1fr 1fr", gap: "0.75rem" }}>
            <label style={lbl}>Issue date
              <input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} style={inp} />
            </label>
            <label style={lbl}>Due date
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inp} />
            </label>
          </div>

          {/* PO Number */}
          <label style={lbl}>
            PO Number <span style={{ color: "#aaa", fontWeight: 400 }}>(optional — client's purchase order reference)</span>
            <input value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="e.g. PO-2026-001" style={inp} />
          </label>

          {/* Project */}
          <div>
            <div style={{ ...lbl, marginBottom: "0.4rem" }}>
              Project <span style={{ color: "#aaa", fontWeight: 400 }}>(optional)</span>
            </div>
            {!showNewProject ? (
              <select
                value={projectId != null ? String(projectId) : ""}
                onChange={e => {
                  const val = e.target.value;
                  if (val === "__new__") { setShowNewProject(true); }
                  else { setProjectId(val ? Number(val) : null); }
                }}
                style={{ ...inp, fontFamily: "Raleway, sans-serif" }}
              >
                <option value="">
                  {projects.length === 0 ? "No projects yet — type to create one" : "— Attach to project (optional) —"}
                </option>
                {projects.map(p => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name}{p.status && p.status !== "active" ? ` (${p.status})` : ""}
                  </option>
                ))}
                <option value="__new__">+ New project…</option>
              </select>
            ) : (
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <input
                  autoFocus
                  value={newProjectName}
                  onChange={e => setNewProjectName(e.target.value)}
                  onKeyDown={async e => {
                    if (e.key === "Enter" && newProjectName.trim()) {
                      await handleCreateProjectInline();
                    }
                    if (e.key === "Escape") { setNewProjectName(""); setShowNewProject(false); }
                  }}
                  placeholder="Project name…"
                  style={inp}
                />
                <button
                  type="button"
                  onClick={handleCreateProjectInline}
                  disabled={!newProjectName.trim()}
                  style={{ ...btnPrimary, whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => { setNewProjectName(""); setShowNewProject(false); }}
                  style={{ ...btnGhost, flexShrink: 0 }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Line items */}
          <div>
            <div style={{ ...lbl, marginBottom: "0.5rem" }}>Line items</div>
            <LineItemsEditor items={lineItems} onChange={setLineItems} sym={sym} />
          </div>

          {/* Notes */}
          <label style={lbl}>Notes
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Payment terms, bank details, etc."
              style={{ ...inp, resize: "vertical" }} />
          </label>

          {/* Recurring */}
          <div style={{ padding: "0.85rem 1rem", background: "#f8f9fa", border: "1px solid #e5e7eb", borderRadius: 8, boxSizing: "border-box", width: "100%" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.6rem", cursor: "pointer", fontWeight: 600, fontSize: "0.88rem", color: "#333" }}>
              <input type="checkbox" checked={isRecurring} onChange={e => setIsRecurring(e.target.checked)} style={{ width: 16, height: 16, accentColor: "#0C2D48", flexShrink: 0 }} />
              Recurring invoice
            </label>
            {isRecurring && (
              <div style={{ display: "grid", gridTemplateColumns: isMobileLayout ? "1fr" : "1fr 1fr", gap: "0.75rem", marginTop: "0.65rem" }}>
                <label style={lbl}>
                  Frequency
                  <select value={recurFreq} onChange={e => setRecurFreq(e.target.value)} style={{ ...inp, fontFamily: "Raleway, sans-serif" }}>
                    {RECUR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <div style={{ fontSize: "0.8rem", color: "#888", alignSelf: "flex-end", paddingBottom: "0.55rem" }}>
                  Next invoice: {fmtDate(addFrequency(issueDate, recurFreq))}
                </div>
              </div>
            )}
          </div>

          {/* Chase task — not applicable for drafts */}
          {invoiceStatus !== "draft" && (
            <div style={{ padding: "0.85rem 1rem", background: "#f0f7ff", border: "1px solid #bfd9f7", borderRadius: 8, boxSizing: "border-box", width: "100%" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "0.6rem", cursor: "pointer", fontWeight: 600, fontSize: "0.88rem", color: "#333" }}>
                <input type="checkbox" checked={createTask} onChange={e => setCreateTask(e.target.checked)} style={{ width: 16, height: 16, accentColor: "#0C2D48", flexShrink: 0 }} />
                Create payment chase task on due date
              </label>
              {createTask && (
                <div style={{ fontSize: "0.78rem", color: "#556", marginTop: "0.4rem", paddingLeft: "1.4rem", lineHeight: 1.5 }}>
                  A task will be added to your schedule on <strong>{fmtDate(dueDate)}</strong> to confirm payment has been received.
                </div>
              )}
            </div>
          )}

          {/* Billable time */}
          {(billableTimeData || billableTimeLoading) && (
            <div style={{ padding: "0.85rem 1rem", background: "#f0f5ff", border: "1px solid #c5d5f5", borderRadius: 8, boxSizing: "border-box", width: "100%" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: showBillableTime ? "0.55rem" : 0 }}>
                <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#1a3a7a" }}>
                  ⏱ Import billable time
                  {billableTimeData && (
                    <span style={{ fontWeight: 400, color: "#aaa", fontSize: "0.75rem", marginLeft: "0.4rem" }}>
                      ({billableTimeData.projects.reduce((s, p) => s + p.entries.length, 0)} entries across {billableTimeData.projects.length} project{billableTimeData.projects.length !== 1 ? "s" : ""})
                    </span>
                  )}
                </div>
                {!billableTimeLoading && (
                  <button
                    type="button"
                    onClick={() => setShowBillableTime(v => !v)}
                    style={{ background: "none", border: "none", color: "#1a3a7a", fontWeight: 700, fontSize: "0.8rem", cursor: "pointer", padding: "0 4px" }}
                  >
                    {showBillableTime ? "▲ Hide" : "▼ Show"}
                  </button>
                )}
              </div>
              {billableTimeLoading && <div style={{ fontSize: "0.8rem", color: "#888" }}>Checking for unbilled time…</div>}
              {showBillableTime && billableTimeData && (
                <>
                  <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
                    <button type="button" onClick={selectAllTime} style={{ fontSize: "0.75rem", padding: "0.2rem 0.55rem", border: "1px solid #c5d5f5", borderRadius: 5, background: "#fff", cursor: "pointer", color: "#1a3a7a" }}>All</button>
                    <button type="button" onClick={deselectAllTime} style={{ fontSize: "0.75rem", padding: "0.2rem 0.55rem", border: "1px solid #c5d5f5", borderRadius: 5, background: "#fff", cursor: "pointer", color: "#888" }}>None</button>
                    {billableTimeData.hourlyRate && (
                      <span style={{ fontSize: "0.75rem", color: "#555", alignSelf: "center", marginLeft: "auto" }}>
                        Rate: £{billableTimeData.hourlyRate}/hr
                      </span>
                    )}
                  </div>
                  {billableTimeData.projects.map(proj => (
                    <div key={proj.projectId} style={{ marginBottom: "0.65rem" }}>
                      <div style={{ fontWeight: 700, fontSize: "0.8rem", color: "#1a3a7a", padding: "0.25rem 0", borderBottom: "1px solid #d0ddf5", marginBottom: "0.3rem" }}>
                        🗂 {proj.projectName} — {proj.totalHours}h total
                        {proj.suggestedAmount ? ` · £${proj.suggestedAmount.toFixed(2)} suggested` : ""}
                      </div>
                      {proj.entries.map(e => {
                        const hrs = Math.round((e.duration / 60) * 100) / 100;
                        const d = new Date(e.startTime);
                        return (
                          <label key={e.id} style={{ display: "flex", alignItems: "center", gap: "0.55rem", padding: "0.28rem 0.1rem", cursor: "pointer", fontSize: "0.82rem", borderBottom: "1px solid #e8eeff" }}>
                            <input
                              type="checkbox"
                              checked={selectedTimeIds.has(e.id)}
                              onChange={() => toggleTimeId(e.id)}
                              style={{ width: 14, height: 14, accentColor: "#0C2D48", flexShrink: 0 }}
                            />
                            <span style={{ flex: 1, color: "#333" }}>{e.note || "Time tracked"}</span>
                            <span style={{ fontSize: "0.75rem", color: "#888", whiteSpace: "nowrap" }}>
                              {d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                            </span>
                            <span style={{ fontWeight: 700, color: "#0C2D48", whiteSpace: "nowrap" }}>{hrs}h</span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
                  {selectedTimeIds.size > 0 && (
                    <div style={{ marginTop: "0.4rem", fontSize: "0.8rem", color: "#1a3a7a", fontWeight: 600 }}>
                      {selectedTimeIds.size} entr{selectedTimeIds.size !== 1 ? "ies" : "y"} selected ·{" "}
                      {Math.round(
                        [...selectedTimeIds].reduce((s, id) => {
                          const e = billableTimeData.projects.flatMap(p => p.entries).find(x => x.id === id);
                          return s + (e ? e.duration / 60 : 0);
                        }, 0) * 100
                      ) / 100}h
                      {billableTimeData.hourlyRate ? ` · £${(
                        [...selectedTimeIds].reduce((s, id) => {
                          const e = billableTimeData.projects.flatMap(p => p.entries).find(x => x.id === id);
                          return s + (e ? (e.duration / 60) * billableTimeData.hourlyRate : 0);
                        }, 0)
                      ).toFixed(2)}` : ""} will be added as line items
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Billable expenses */}
          {unbilledBillable.length > 0 && (
            <div style={{ padding: "0.85rem 1rem", background: "#fffdf5", border: "1px solid #f0e6b0", borderRadius: 8, boxSizing: "border-box", width: "100%" }}>
              <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#6b4e00", marginBottom: "0.55rem" }}>
                📎 Add billable expenses{" "}
                <span style={{ fontWeight: 400, color: "#aaa", fontSize: "0.75rem" }}>
                  ({unbilledBillable.length} unbilled)
                </span>
              </div>
              {/* Filter by project within this section */}
              {projects.length > 0 && (
                <select
                  value={expProjectFilter}
                  onChange={e => setExpProjectFilter(e.target.value)}
                  style={{ ...inp, marginBottom: "0.55rem", fontSize: "0.82rem", fontFamily: "Raleway, sans-serif" }}
                >
                  <option value="">All projects</option>
                  {projects.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                </select>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                {unbilledBillable
                  .filter(e => !expProjectFilter || String(e.project_id) === expProjectFilter)
                  .map(exp => (
                    <label key={exp.id} style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.3rem 0.1rem", cursor: "pointer", fontSize: "0.85rem", borderBottom: "1px solid #f5ecc8" }}>
                      <input
                        type="checkbox"
                        checked={selectedExpenses.includes(exp.id)}
                        onChange={e => setSelectedExpenses(prev => e.target.checked ? [...prev, exp.id] : prev.filter(id => id !== exp.id))}
                        style={{ width: 15, height: 15, accentColor: "#0C2D48", flexShrink: 0 }}
                      />
                      <span style={{ flex: 1 }}>{exp.title}</span>
                      <span style={{ fontWeight: 700, color: "#0C2D48", whiteSpace: "nowrap" }}>
                        £{Number(exp.amount).toFixed(2)}
                      </span>
                      <span style={{ fontSize: "0.72rem", color: "#aaa", whiteSpace: "nowrap" }}>
                        {exp.category}
                      </span>
                    </label>
                  ))}
              </div>
              {selectedExpenses.length > 0 && (
                <div style={{ marginTop: "0.5rem", fontSize: "0.8rem", color: "#6b4e00", fontWeight: 600 }}>
                  {selectedExpenses.length} selected · £{
                    unbilledBillable
                      .filter(e => selectedExpenses.includes(e.id))
                      .reduce((s, e) => s + Number(e.amount), 0)
                      .toFixed(2)
                  } will be added as line items
                </div>
              )}
            </div>
          )}
        </div>

        {formError && (
          <div style={{ marginTop: "1rem", padding: "0.55rem 0.8rem", background: "#fde8e8", border: "1px solid #f5a5a5", borderRadius: 7, fontSize: "0.85rem", color: "#A12710", fontWeight: 600 }}>
            {formError}
          </div>
        )}

        <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem" }}>
          <button onClick={handleSave} style={{ ...btnPrimary, opacity: canSubmit ? 1 : 0.55, cursor: canSubmit ? "pointer" : "not-allowed" }} disabled={!canSubmit}>
            {saving ? "Saving…" : (isEdit ? "Save Changes" : "Create Invoice")}
          </button>
          <button onClick={onClose} style={btnGhost} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Invoice detail modal ─────────────────────────────────────────────────────
function InvoiceDetailModal({ invoice, projects, paymentDetails, bookingUsername, gated, canApprove, onSubmitForApproval, onApprove, onReject, onClose, onEdit, onMarkPaid, onVoid, onUnvoid, onDelete, onUpdateReminders, onUpdateProject }) {
  const sym = currencySymbol(invoice.currency);
  const totals = calcTotals(invoice.lineItems);
  const st = STATUS[invoice.status] || STATUS.pending;
  // TeamBooks approval state (camelCase — serializeInvoice spreads the raw model).
  const approvalStatus = invoice.approvalStatus || "approved";
  const isPending  = approvalStatus === "pending";
  const isRejected = approvalStatus === "rejected";
  const [reminders, setReminders] = useState(invoice.reminders ?? []);
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState(null);
  const [copied, setCopied] = useState(false);

  function copyInvoiceLink() {
    const url = bookingUsername
      ? `${window.location.origin}/invoice/${encodeURIComponent(bookingUsername)}/${encodeURIComponent(invoice.number)}`
      : null;
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  async function sendViaEmail() {
    setSending(true);
    setSendMsg(null);
    const s = currencySymbol(invoice.currency);
    const totals = calcTotals(invoice.lineItems);
    const subject = `Invoice ${invoice.number} — ${fmtMoney(totals.total, s)}`;
    // Deep-link to the public invoice page (never a Checkout session URL).
    const payUrl = `${window.location.origin}/invoice/${encodeURIComponent(bookingUsername)}/${encodeURIComponent(invoice.number)}`;
    // Same resolver the public page uses (CONNECT_PAYMENTS_ENABLED AND connected
    // AND payable). On any failure omit the button — never show a pay CTA we
    // can't confirm.
    let paymentEnabled = false;
    try {
      const pr = await fetch(`/api/invoices/public/${encodeURIComponent(bookingUsername)}/${encodeURIComponent(invoice.number)}`);
      if (pr.ok) { const pd = await pr.json(); paymentEnabled = !!pd.paymentEnabled; }
    } catch { paymentEnabled = false; }
    const rows = invoice.lineItems.map(r => {
      const amt = r.qty * r.unitPrice * (1 + r.tax / 100);
      return `<tr><td style="padding:6px 10px;border-bottom:1px solid #f0f0f0">${r.description}</td><td style="padding:6px 10px;text-align:right;border-bottom:1px solid #f0f0f0">${r.qty}</td><td style="padding:6px 10px;text-align:right;border-bottom:1px solid #f0f0f0">${fmtMoney(amt, s)}</td></tr>`;
    }).join('');
    const payButton = paymentEnabled
      ? `<table cellpadding="0" cellspacing="0" border="0" style="margin:22px 0 4px"><tr><td align="center" style="background:#5352ed;border-radius:8px"><a href="${payUrl}" target="_blank" style="display:inline-block;padding:13px 30px;font-family:Arial,sans-serif;font-size:16px;font-weight:700;color:#ffffff;text-decoration:none">Pay online — ${fmtMoney(totals.total, s)}</a></td></tr></table>`
      : '';
    const viewLink = `<p style="margin-top:${paymentEnabled ? '4' : '16'}px;font-size:13px"><a href="${payUrl}" target="_blank" style="color:#5352ed;text-decoration:underline">View invoice online</a></p>`;
    const html = `<div style="font-family:Arial,sans-serif;color:#333"><p>Hi ${invoice.contactName},</p><p>Please find invoice <strong>${invoice.number}</strong> below, due <strong>${fmtDate(invoice.dueDate)}</strong>.</p><table style="width:100%;border-collapse:collapse;margin:16px 0"><thead><tr style="background:#f8f9fa"><th style="padding:7px 10px;text-align:left;font-size:12px;color:#888">Description</th><th style="padding:7px 10px;text-align:right;font-size:12px;color:#888">Qty</th><th style="padding:7px 10px;text-align:right;font-size:12px;color:#888">Amount</th></tr></thead><tbody>${rows}</tbody></table><div style="text-align:right;margin-top:4px"><div style="font-size:13px;color:#888">Subtotal: ${fmtMoney(totals.subtotal, s)}</div>${totals.tax > 0 ? `<div style="font-size:13px;color:#888">Tax: ${fmtMoney(totals.tax, s)}</div>` : ''}<div style="font-size:18px;font-weight:900;color:#0C2D48;margin-top:4px">Total: ${fmtMoney(totals.total, s)}</div></div>${payButton}${viewLink}${paymentDetails ? `<div style="margin-top:18px;padding:12px 14px;background:#f9f9f9;border-radius:6px;font-size:13px"><strong>Payment Details</strong><br/>${paymentDetails.replace(/\n/g,'<br/>')}</div>` : ''}${invoice.notes ? `<p style="margin-top:14px;font-size:13px;color:#555">${invoice.notes}</p>` : ''}<p style="margin-top:20px">Please don't hesitate to get in touch if you have any questions.</p><p>Thank you,</p></div>`;
    try {
      const res = await api('/api/email/send', { method: 'POST', body: JSON.stringify({ to: invoice.contactEmail, subject, body: html }) });
      setSendMsg(res.ok ? '✓ Sent' : 'Failed to send');
    } catch { setSendMsg('Failed to send'); }
    finally { setSending(false); }
  }

  function togglePreset(preset) {
    const sendDate = reminderDate(invoice.dueDate, preset.daysOffset);
    const exists = reminders.find(r => r.daysOffset === preset.daysOffset);
    let updated;
    if (exists) {
      updated = reminders.filter(r => r.daysOffset !== preset.daysOffset);
    } else {
      updated = [...reminders, { daysOffset: preset.daysOffset, label: preset.label, sendDate, status: "pending" }];
    }
    updated.sort((a, b) => a.daysOffset - b.daysOffset);
    setReminders(updated);
    onUpdateReminders(invoice.id, updated);
  }

  function cancelReminder(daysOffset) {
    const updated = reminders.map(r => r.daysOffset === daysOffset ? { ...r, status: "cancelled" } : r);
    setReminders(updated);
    onUpdateReminders(invoice.id, updated);
  }

  const pendingCount = reminders.filter(r => r.status === "pending").length;

  return (
    <div className="modal-overlay">
      <div className="modal-content neutral" style={{ maxWidth: 620, width: "95vw", maxHeight: "90vh", overflowY: "auto" }}>
        <button className="close-button" onClick={onClose}>✕</button>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1.25rem" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "1.25rem" }}>{invoice.number}</h3>
            <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginTop: "0.3rem" }}>
              {invoice.recurring && (
                <span style={{ fontSize: "0.75rem", background: "#e8f4fb", color: "#2a6080", border: "1px solid #82B3CC", borderRadius: 8, padding: "0.1rem 0.5rem", fontWeight: 600 }}>
                  🔁 Recurring · {invoice.recurring.frequency}
                </span>
              )}
              {invoice.linkedTaskId && (
                <span style={{ fontSize: "0.75rem", background: "#f0f7ff", color: "#2a4a80", border: "1px solid #bfd9f7", borderRadius: 8, padding: "0.1rem 0.5rem", fontWeight: 600 }}>
                  📋 Chase task active
                </span>
              )}
              {pendingCount > 0 && (
                <span style={{ fontSize: "0.75rem", background: "#fff8ee", color: "#7a4a10", border: "1px solid #FBC490", borderRadius: 8, padding: "0.1rem 0.5rem", fontWeight: 600 }}>
                  🔔 {pendingCount} reminder{pendingCount !== 1 ? "s" : ""} scheduled
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
            {isPending && (
              <span style={{ background: "#fff8ee", color: "#7a4a10", border: "1px solid #FBC490", borderRadius: 8, padding: "0.2rem 0.75rem", fontWeight: 700, fontSize: "0.82rem" }}>
                ⏳ Awaiting approval
              </span>
            )}
            {isRejected && (
              <span title={invoice.rejectionReason || undefined} style={{ background: "#fde8e8", color: "#A12710", border: "1px solid #f5a5a5", borderRadius: 8, padding: "0.2rem 0.75rem", fontWeight: 700, fontSize: "0.82rem" }}>
                ✕ Rejected
              </span>
            )}
            <span style={{ background: st.bg, color: st.text, border: `1px solid ${st.border}`, borderRadius: 8, padding: "0.2rem 0.75rem", fontWeight: 700, fontSize: "0.82rem" }}>
              {st.label}
            </span>
          </div>
        </div>

        {/* Rejection reason banner (submitter sees why + can edit/resubmit) */}
        {isRejected && invoice.rejectionReason && (
          <div style={{ margin: "0 0 1rem", padding: "0.6rem 0.9rem", background: "#fde8e8", border: "1px solid #f5a5a5", borderRadius: 8, fontSize: "0.85rem", color: "#A12710" }}>
            <strong>Rejected:</strong> {invoice.rejectionReason}
          </div>
        )}

        {/* Meta */}
        <div className="inv-dates-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem 1.5rem", marginBottom: "1.25rem", fontSize: "0.88rem" }}>
          <div><span style={{ color: "#aaa" }}>Bill to:</span> <strong>{invoice.contactName}</strong>{invoice.contactCompany ? `, ${invoice.contactCompany}` : ""}</div>
          <div><span style={{ color: "#aaa" }}>Email:</span> {invoice.contactEmail || "—"}</div>
          <div><span style={{ color: "#aaa" }}>Issued:</span> {fmtDate(invoice.issueDate)}</div>
          <div><span style={{ color: "#aaa" }}>Due:</span> {fmtDate(invoice.dueDate)}</div>
          {invoice.paidAt && <div><span style={{ color: "#aaa" }}>Paid:</span> {fmtDate(invoice.paidAt)}</div>}
          {invoice.recurring && <div><span style={{ color: "#aaa" }}>Next:</span> {fmtDate(invoice.recurring.nextDate)}</div>}
          {invoice.poNumber && <div><span style={{ color: "#aaa" }}>PO Ref:</span> <strong>{invoice.poNumber}</strong></div>}
          <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ color: "#aaa" }}>Project:</span>
            <select
              value={invoice.project_id != null ? String(invoice.project_id) : ""}
              onChange={e => onUpdateProject && onUpdateProject(invoice.id, e.target.value ? Number(e.target.value) : null)}
              style={{ fontSize: "0.85rem", border: "1px solid #ddd", borderRadius: 5, padding: "0.15rem 0.5rem", fontFamily: "Raleway, sans-serif", background: "#fff", color: "#333" }}
            >
              <option value="">— No project —</option>
              {(projects || []).map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
            </select>
          </div>
        </div>

        {/* Line items */}
        <div className="inv-view-table" style={{ overflowX: "auto", borderRadius: 8, marginBottom: "0.75rem" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", minWidth: 360 }}>
            <thead>
              <tr style={{ background: "#0C2D48", color: "#fff" }}>
                {["Description", "Qty", "Unit", "Tax", "Amount"].map((h, i) => (
                  <th key={h} style={{ padding: "0.5rem 0.75rem", textAlign: i > 0 ? "right" : "left", fontWeight: 700, fontSize: "0.8rem" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoice.lineItems.map(r => {
                const amt = r.qty * r.unitPrice * (1 + r.tax / 100);
                return (
                  <tr key={r.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td data-label="Description" style={{ padding: "0.5rem 0.75rem" }}>{r.description}</td>
                    <td data-label="Qty" style={{ padding: "0.5rem 0.75rem", textAlign: "right" }}>{r.qty}</td>
                    <td data-label="Unit" style={{ padding: "0.5rem 0.75rem", textAlign: "right" }}>{fmtMoney(r.unitPrice, sym)}</td>
                    <td data-label="Tax" style={{ padding: "0.5rem 0.75rem", textAlign: "right" }}>{r.tax}%</td>
                    <td data-label="Amount" style={{ padding: "0.5rem 0.75rem", textAlign: "right", fontWeight: 600 }}>{fmtMoney(amt, sym)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div style={{ marginLeft: "auto", width: 220, fontSize: "0.88rem", marginBottom: "1rem" }}>
          {[["Subtotal", totals.subtotal], ["Tax", totals.tax]].map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "0.2rem 0", color: "#666" }}>
              <span>{l}</span><span>{fmtMoney(v, sym)}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "0.5rem 0 0", fontWeight: 900, fontSize: "1.05rem", color: "#0C2D48", borderTop: "2px solid #0C2D48", marginTop: "0.3rem" }}>
            <span>Total ({invoice.currency})</span><span>{fmtMoney(totals.total, sym)}</span>
          </div>
        </div>

        {invoice.notes && (
          <div style={{ padding: "0.65rem 0.9rem", background: "#f9f9f9", borderRadius: 7, fontSize: "0.85rem", color: "#555", marginBottom: "1rem" }}>
            <strong style={{ display: "block", marginBottom: "0.25rem", color: "#333" }}>Notes</strong>
            {invoice.notes}
          </div>
        )}

        {paymentDetails && (
          <div style={{ padding: "0.65rem 0.9rem", background: "#f9f9f9", borderRadius: 7, fontSize: "0.85rem", color: "#555", marginBottom: "1rem", whiteSpace: "pre-wrap" }}>
            <strong style={{ display: "block", marginBottom: "0.25rem", color: "#333" }}>Payment Details</strong>
            {paymentDetails}
          </div>
        )}

        {/* Reminders */}
        {invoice.status !== "paid" && (
          <div style={{ padding: "0.85rem 1rem", background: "#fffdf5", border: "1px solid #f0e6b0", borderRadius: 8, marginBottom: "1rem" }}>
            <div style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: "0.6rem", color: "#6b4e00" }}>
              🔔 Email reminders <span style={{ fontWeight: 400, color: "#999", fontSize: "0.75rem" }}>(backend pending — toggles saved locally)</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.6rem" }}>
              {REMINDER_PRESETS.map(preset => {
                const active = reminders.find(r => r.daysOffset === preset.daysOffset);
                const isCancelled = active?.status === "cancelled";
                return (
                  <button
                    key={preset.daysOffset}
                    onClick={() => !isCancelled && togglePreset(preset)}
                    style={{
                      padding: "0.25rem 0.65rem", borderRadius: 6, fontSize: "0.75rem", fontWeight: 600,
                      cursor: isCancelled ? "default" : "pointer", opacity: isCancelled ? 0.4 : 1,
                      border: active && !isCancelled ? "1px solid #e4a000" : "1px solid #ddd",
                      background: active && !isCancelled ? "#fff3cc" : "#f8f8f8",
                      color: active && !isCancelled ? "#7a4a00" : "#555",
                      textDecoration: isCancelled ? "line-through" : "none",
                    }}
                  >
                    {active && !isCancelled ? "✓ " : ""}{preset.label}
                  </button>
                );
              })}
            </div>
            {reminders.filter(r => r.status !== "cancelled").length > 0 && (
              <div style={{ fontSize: "0.78rem", color: "#666" }}>
                {reminders.filter(r => r.status !== "cancelled").map(r => (
                  <div key={r.daysOffset} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.2rem 0", borderTop: "1px solid #f0e6b0" }}>
                    <span>
                      <span style={{ background: r.status === "sent" ? "#eaf6ef" : "#fff8ee", color: r.status === "sent" ? "#1e6645" : "#7a4a10", border: `1px solid ${r.status === "sent" ? "#6abf85" : "#FBC490"}`, borderRadius: 4, padding: "0 0.3rem", fontSize: "0.7rem", fontWeight: 700, marginRight: "0.4rem" }}>
                        {r.status === "sent" ? "Sent" : "Pending"}
                      </span>
                      {r.label} · {fmtDate(r.sendDate)}
                    </span>
                    {r.status === "pending" && (
                      <button onClick={() => cancelReminder(r.daysOffset)} style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: "0.8rem", padding: "0 2px" }}>✕</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          <button onClick={() => printInvoice(invoice, paymentDetails)} style={{ ...btnPrimary, background: "#0C2D48", color: "#fff" }}>
            ⬇ Export PDF
          </button>
          {bookingUsername && (
            <button onClick={copyInvoiceLink} style={btnSecondary}>
              {copied ? "✓ Copied" : "🔗 Copy link"}
            </button>
          )}
          {/* Direct client-send: hidden for gated members (they submit for approval). */}
          {invoice.contactEmail && invoice.status !== "paid" && !gated && (
            <>
              <button
                onClick={sendViaEmail}
                disabled={sending}
                style={{ ...btnPrimary, background: "#E4E69B", color: "#0C2D48", border: "1px solid #d4d48b", opacity: sending ? 0.7 : 1 }}
              >
                {sending ? "Sending…" : "📧 Send Invoice"}
              </button>
              {sendMsg && (
                <span style={{ fontSize: "0.8rem", fontWeight: 600, color: sendMsg.startsWith("✓") ? "#1e6645" : "#A12710" }}>
                  {sendMsg}
                </span>
              )}
            </>
          )}

          {/* Gated member: explicit Submit-for-approval (Option B); locked while pending. */}
          {gated && isPending && (
            <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#7a4a10" }}>
              ⏳ Awaiting owner/finance approval
            </span>
          )}
          {gated && !isPending && invoice.status !== "paid" && (
            <button
              onClick={() => onSubmitForApproval(invoice.id)}
              style={{ ...btnPrimary, background: "#E4E69B", color: "#0C2D48", border: "1px solid #d4d48b" }}
            >
              {isRejected ? "↻ Resubmit for approval" : "📤 Submit for approval"}
            </button>
          )}

          {/* Owner/finance: approve (→ sends) or reject a pending invoice. */}
          {canApprove && isPending && (
            <>
              <button onClick={() => onApprove(invoice.id)} style={{ ...btnPrimary, background: "#eaf6ef", color: "#1e6645", border: "1px solid #6abf85" }}>
                ✓ Approve &amp; send
              </button>
              <button onClick={() => onReject(invoice.id)} style={btnDanger}>
                ✕ Reject
              </button>
            </>
          )}

          {invoice.status !== "paid" && (
            <button onClick={() => onMarkPaid(invoice.id)} style={{ ...btnPrimary, background: "#eaf6ef", color: "#1e6645", border: "1px solid #6abf85" }}>
              ✓ Mark as Paid
            </button>
          )}
          {/* Block 171: Void an active invoice, or Restore (un-void) a cancelled one. */}
          {invoice.status !== "paid" && invoice.status !== "cancelled" && (
            <button onClick={() => onVoid(invoice.id)} style={btnSecondary}>Void</button>
          )}
          {invoice.status === "cancelled" && (
            <button onClick={() => onUnvoid(invoice.id)} style={{ ...btnSecondary, borderColor: "#6abf85", color: "#1e6645" }}>Restore</button>
          )}
          {/* Editing a submitted invoice is locked for the member until it's decided. */}
          {!(gated && isPending) && (
            <button onClick={() => { onEdit(invoice); onClose(); }} style={btnSecondary}>Edit</button>
          )}
          <button onClick={() => { onDelete(invoice.id); onClose(); }} style={btnDanger}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color, isMobileLayout }) {
  return (
    <div style={{ flex: isMobileLayout ? '1 1 100%' : 1, minWidth: isMobileLayout ? 0 : 130, padding: "0.9rem 1rem", background: "#fff", border: "1px solid #35454C", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
      <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.3rem" }}>{label}</div>
      <div style={{ fontSize: "1.35rem", fontWeight: 900, color: color || "#0C2D48" }}>{value}</div>
      {sub && <div style={{ fontSize: "0.75rem", color: "#aaa", marginTop: "0.15rem" }}>{sub}</div>}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
function InvoicingDashboard({ initialTab = "All", onOpenProjectFinancials, openFormWithProjectId, onFormOpened, billTimeEntries, onBillTimeFormOpened }) {
  const isMobileLayout = typeof window !== 'undefined'
    && (window.innerWidth <= 1024 || window.matchMedia('(pointer: coarse)').matches);
  const { t } = useTranslation('common');
  const { currentUser } = useCurrentUser();
  const paymentDetails = currentUser?.preferences?.invoicingPrefs?.paymentDetails || '';
  const bookingUsername = currentUser?.bookingUsername || null;
  const { invoices, setInvoices, refresh: refreshInvoices } = useInvoices();
  const { refresh: refreshExpenses } = useExpenses();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [preselectedProjectId, setPreselectedProjectId] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  // Block D20: an invoice deep-link opens that invoice's detail (once the list is loaded).
  const [dlInvoiceId, dlClearInvoice] = useDeepLinkTarget('invoice');
  useEffect(() => {
    if (!dlInvoiceId || !invoices.length) return;
    const inv = invoices.find(i => String(i.id) === String(dlInvoiceId));
    if (inv) { setViewInvoice(inv); dlClearInvoice(); clearPendingDeepLink(); } // D23: retire the deep link only once opened
  }, [dlInvoiceId, invoices]); // eslint-disable-line react-hooks/exhaustive-deps
  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [toast, setToast] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const projectsLookup = Object.fromEntries(allProjects.map(p => [p.id, p.name]));

  useEffect(() => {
    let alive = true;

    const loadProjects = async () => {
      try {
        const projects = await getProjects();
        if (alive) setAllProjects(Array.isArray(projects) ? projects : []);
      } catch {
        if (alive) setAllProjects([]);
      }
    };

    loadProjects();

    const onProjectsUpdated = () => {
      loadProjects();
    };
    window.addEventListener('aidey:projectsUpdated', onProjectsUpdated);

    return () => {
      alive = false;
      window.removeEventListener('aidey:projectsUpdated', onProjectsUpdated);
    };
  }, []);

  const [preselectedTimeEntries, setPreselectedTimeEntries] = useState(null);

  // Exchange rates (GBP base, 1-hour TTL via backend)
  const [fxRates, setFxRates] = useState(null);
  const [fxTimestamp, setFxTimestamp] = useState(null);
  const [fxStale, setFxStale] = useState(false);
  useEffect(() => {
    api('/api/invoices/exchange-rates').then(r => r.json()).then(d => {
      if (d?.rates) { setFxRates(d.rates); setFxTimestamp(d.timestamp); setFxStale(!!d.stale); }
    }).catch(() => {});
  }, []);

  // Auto-open invoice form when a project shortcut requests it
  useEffect(() => {
    if (openFormWithProjectId != null) {
      setEditTarget(null);
      setPreselectedProjectId(openFormWithProjectId);
      setShowForm(true);
      if (onFormOpened) onFormOpened();
    }
  }, [openFormWithProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open invoice form with pre-selected time entries from TimesheetView
  useEffect(() => {
    if (billTimeEntries?.length) {
      setEditTarget(null);
      setPreselectedProjectId(null);
      setPreselectedTimeEntries(billTimeEntries);
      setShowForm(true);
      if (onBillTimeFormOpened) onBillTimeFormOpened();
    }
  }, [billTimeEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  // TeamBooks invoice approval. canApproveExpenses = owner/finance (the same
  // financialStanding privilege that gates invoices). A plain business member
  // (has a business but can't approve) is "gated": they submit for approval
  // instead of sending directly. Solo users have no teamId → gated = false, so
  // no approval affordances appear at all.
  const canApproveInvoices = !!currentUser?.canApproveExpenses;
  const isBusinessMember = !!(currentUser?.teamId || currentUser?.isCoveredMember);
  const invoiceGated = isBusinessMember && !canApproveInvoices;

  async function submitInvoiceForApproval(id) {
    // A member's "Submit for approval" reuses PUT status:'sent'; the backend
    // gate converts it to approval_status='pending' (stays a draft, no send).
    setInvoices(prev => prev.map(i => i.id === id ? { ...i, approvalStatus: 'pending' } : i));
    setViewInvoice(null);
    showToast("Invoice submitted for approval.");
    try {
      const res = await api(`/api/invoices/${id}`, { method: 'PUT', body: JSON.stringify({ status: 'sent' }) });
      if (res.ok) { const d = await res.json(); setInvoices(prev => prev.map(i => i.id === id ? d.invoice : i)); }
      else refreshInvoices();
    } catch (err) { console.error('Failed to submit invoice:', err); refreshInvoices(); }
  }

  async function approveInvoice(id) {
    setInvoices(prev => prev.map(i => i.id === id ? { ...i, approvalStatus: 'approved', status: 'sent', rejectionReason: null } : i));
    setViewInvoice(null);
    showToast("Invoice approved & sent.");
    try {
      const res = await api(`/api/invoices/${id}/approve`, { method: 'PUT' });
      if (res.ok) { const d = await res.json(); setInvoices(prev => prev.map(i => i.id === id ? d.invoice : i)); }
      else refreshInvoices();
    } catch (err) { console.error('Failed to approve invoice:', err); refreshInvoices(); }
  }

  async function rejectInvoice(id) {
    const reason = window.prompt('Reason for rejection (optional):');
    if (reason === null) return; // cancelled
    setInvoices(prev => prev.map(i => i.id === id ? { ...i, approvalStatus: 'rejected', status: 'draft', rejectionReason: reason || null } : i));
    setViewInvoice(null);
    showToast("Invoice rejected.");
    try {
      const res = await api(`/api/invoices/${id}/reject`, { method: 'PUT', body: JSON.stringify({ reason }) });
      if (res.ok) { const d = await res.json(); setInvoices(prev => prev.map(i => i.id === id ? d.invoice : i)); }
      else refreshInvoices();
    } catch (err) { console.error('Failed to reject invoice:', err); refreshInvoices(); }
  }

  async function saveInvoice(inv) {
    const { _createTask, _selectedExpenseIds, _selectedTimeIds, ...clean } = inv;

    // Mark billable expenses as billed (fire-and-forget)
    if (_selectedExpenseIds?.length) {
      api('/api/expenses/bulk-billed', {
        method: 'PUT',
        body: JSON.stringify({ ids: _selectedExpenseIds }),
      }).then(() => refreshExpenses()).catch(err => console.error('Failed to mark expenses billed:', err));
    }

    // Mark billable time entries as billed (fire-and-forget)
    if (_selectedTimeIds?.length) {
      api('/api/time-entries/bulk-billed', {
        method: 'PUT',
        body: JSON.stringify({ ids: _selectedTimeIds }),
      }).catch(err => console.error('Failed to mark time entries billed:', err));
    }

    // Create payment chase task — wrapped in its own try/catch so a failure here
    // never prevents the invoice itself from being saved or the modal from closing
    let finalInv = clean;
    let taskCreated = false;
    if (_createTask && !clean.linkedTaskId) {
      try {
        const taskId = await createChaseTask(clean);
        finalInv = { ...clean, linkedTaskId: taskId };
        taskCreated = true;
        window.dispatchEvent(new Event('aidey:tasksUpdated'));
      } catch (err) {
        console.error('Chase task creation failed, continuing without it:', err);
      }
    }

    // Build API payload (map project_id → projectId for backend)
    const payload = { ...finalInv, projectId: finalInv.project_id };
    delete payload.project_id;

    const isEdit = invoices.some(i => i.id === finalInv.id);
    try {
      if (isEdit) {
        const res = await api(`/api/invoices/${finalInv.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `Server error (${res.status})`);
        }
        const data = await res.json();
        if (data.invoice) {
          const updated = { ...finalInv, id: Number(data.invoice.id) };
          setInvoices(prev => prev.map(i => i.id === finalInv.id ? updated : i));
        }
      } else {
        const res = await api('/api/invoices', { method: 'POST', body: JSON.stringify(payload) });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `Server error (${res.status})`);
        }
        const data = await res.json();
        if (data.invoice) {
          const created = { ...finalInv, id: Number(data.invoice.id) };
          setInvoices(prev => [created, ...prev]);
        }
      }
      // Only close and toast on confirmed success
      setShowForm(false);
      setEditTarget(null);
      showToast(`Invoice ${finalInv.number} ${isEdit ? 'updated' : 'created'}.${taskCreated ? ' Chase task created.' : ''}`);
    } catch (err) {
      console.error('Failed to save invoice:', err);
      showToast(`Could not save invoice — ${err.message}`);
      refreshInvoices();
      // Modal stays open so the user can see the error and retry
    }
  }

  async function markPaid(id) {
    const inv = invoices.find(i => i.id === id);
    // Block 171: moving a cancelled invoice to paid must be deliberate — a voided invoice being
    // silently marked paid would be wrong. Name exactly what is happening before proceeding.
    if (inv?.status === 'cancelled' &&
        !window.confirm(`Invoice ${inv?.number || id} is currently cancelled. Mark it as paid? This will un-void the invoice and record it as paid.`)) {
      return;
    }
    const paidAt = new Date().toISOString().slice(0, 10);
    // Optimistic update
    setInvoices(prev => {
      const inv = prev.find(i => i.id === id);
      if (inv?.linkedTaskId) completeLinkedTask(inv.linkedTaskId);
      return prev.map(i => i.id === id ? { ...i, status: "paid", paidAt } : i);
    });
    setViewInvoice(v => v && v.id === id ? { ...v, status: "paid", paidAt } : v);
    showToast("Invoice marked as paid.");
    api(`/api/invoices/${id}`, { method: 'PUT', body: JSON.stringify({ status: 'paid', paidAt }) })
      .catch(err => { console.error('Failed to mark invoice paid:', err); refreshInvoices(); });
  }

  // Block 171: Void — mark an invoice cancelled. Confirm-gated. A cancelled invoice can no longer be
  // paid online (backend allow-list), but it is NOT deleted and can be restored.
  async function voidInvoice(id) {
    const inv = invoices.find(i => i.id === id);
    if (!window.confirm(`Void invoice ${inv?.number || id}? It will be marked cancelled and can no longer be paid online. You can restore it later.`)) return;
    setInvoices(prev => prev.map(i => i.id === id ? { ...i, status: "cancelled" } : i));
    setViewInvoice(v => v && v.id === id ? { ...v, status: "cancelled" } : v);
    showToast("Invoice voided.");
    api(`/api/invoices/${id}`, { method: 'PUT', body: JSON.stringify({ status: 'cancelled' }) })
      .catch(err => { console.error('Failed to void invoice:', err); refreshInvoices(); });
  }

  // Un-void: move a cancelled invoice back to Sent. Confirm-gated and names what is happening,
  // because leaving 'cancelled' is a deliberate act (the guard blocks online payment while voided).
  async function unvoidInvoice(id) {
    const inv = invoices.find(i => i.id === id);
    if (!window.confirm(`Restore invoice ${inv?.number || id}? It will move back to Sent and can be paid online again.`)) return;
    setInvoices(prev => prev.map(i => i.id === id ? { ...i, status: "sent" } : i));
    setViewInvoice(v => v && v.id === id ? { ...v, status: "sent" } : v);
    showToast("Invoice restored.");
    api(`/api/invoices/${id}`, { method: 'PUT', body: JSON.stringify({ status: 'sent' }) })
      .catch(err => { console.error('Failed to restore invoice:', err); refreshInvoices(); });
  }

  async function updateReminders(invoiceId, reminders) {
    setInvoices(prev => prev.map(i => i.id === invoiceId ? { ...i, reminders } : i));
    setViewInvoice(v => v && v.id === invoiceId ? { ...v, reminders } : v);
    api(`/api/invoices/${invoiceId}`, { method: 'PUT', body: JSON.stringify({ reminders }) })
      .catch(err => console.error('Failed to update reminders:', err));
  }

  async function updateProject(invoiceId, project_id) {
    setInvoices(prev => prev.map(i => i.id === invoiceId ? { ...i, project_id } : i));
    setViewInvoice(v => v && v.id === invoiceId ? { ...v, project_id } : v);
    api(`/api/invoices/${invoiceId}`, { method: 'PUT', body: JSON.stringify({ projectId: project_id }) })
      .catch(err => console.error('Failed to update project:', err));
  }

  async function deleteInvoice(id) {
    setInvoices(prev => prev.filter(i => i.id !== id));
    showToast("Invoice deleted.");
    api(`/api/invoices/${id}`, { method: 'DELETE' })
      .catch(err => { console.error('Failed to delete invoice:', err); refreshInvoices(); });
  }

  // Filtered list
  const filtered = useMemo(() => {
    return invoices.filter(inv => {
      const q = search.toLowerCase();
      const matchSearch = !q
        || inv.number?.toLowerCase().includes(q)
        || inv.contactName?.toLowerCase().includes(q)
        || inv.contactCompany?.toLowerCase().includes(q);
      const matchTab = activeTab === "All"
        ? true
        : activeTab === "Recurring"
          ? !!inv.recurring && inv.status !== "draft"
          : activeTab === "Drafts"
            ? inv.status === "draft"
            : inv.status === activeTab.toLowerCase();
      const matchProject = !projectFilter
        ? true
        : projectFilter === "none"
          ? !inv.project_id
          : String(inv.project_id) === projectFilter;
      return matchSearch && matchTab && matchProject;
    });
  }, [invoices, search, activeTab, projectFilter]);

  // Base currency for stats display (falls back to GBP)
  const baseCurrency = currentUser?.baseCurrency || currentUser?.currencyDisplay || "GBP";
  const baseSym = currencySymbol(baseCurrency);

  // Convert an invoice amount to baseCurrency using GBP-based rates
  function toBase(amount, fromCurrency) {
    if (!fxRates || fromCurrency === baseCurrency) return amount;
    const gbpAmount = fromCurrency === "GBP" ? amount : amount / (fxRates[fromCurrency] || 1);
    return baseCurrency === "GBP" ? gbpAmount : gbpAmount * (fxRates[baseCurrency] || 1);
  }

  // Stats — exclude drafts (they are not yet real invoices)
  const stats = useMemo(() => {
    const live = invoices.filter(i => i.status !== "draft");
    const sumBase = (list) => list.reduce((s, inv) => s + toBase(calcTotals(inv.lineItems).total, inv.currency || "GBP"), 0);

    // Per-currency breakdown
    const byCurrency = {};
    live.forEach(inv => {
      const c = inv.currency || "GBP";
      if (!byCurrency[c]) byCurrency[c] = 0;
      byCurrency[c] += calcTotals(inv.lineItems).total;
    });
    const currencies = Object.keys(byCurrency);
    const multiCurrency = currencies.length > 1;

    return {
      total: fmtMoney(sumBase(live), baseSym),
      paid: fmtMoney(sumBase(live.filter(i => i.status === "paid")), baseSym),
      pending: fmtMoney(sumBase(live.filter(i => i.status === "pending")), baseSym),
      overdue: fmtMoney(sumBase(live.filter(i => i.status === "overdue")), baseSym),
      overdueCount: live.filter(i => i.status === "overdue").length,
      draftCount: invoices.filter(i => i.status === "draft").length,
      multiCurrency,
      fxNote: multiCurrency && fxTimestamp ? `Converted to ${baseCurrency} · rates as of ${new Date(fxTimestamp).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : null,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, fxRates, fxTimestamp, baseCurrency]);

  // Income breakdown by project (amounts converted to baseCurrency)
  const byProject = useMemo(() => {
    if (allProjects.length === 0) return [];
    const map = {};
    invoices.forEach(inv => {
      const pid = inv.project_id;
      if (!pid) return;
      if (!map[pid]) map[pid] = { invoiced: 0, received: 0, count: 0 };
      const total = toBase(calcTotals(inv.lineItems).total, inv.currency || "GBP");
      map[pid].invoiced += total;
      map[pid].count += 1;
      if (inv.status === "paid") map[pid].received += total;
    });
    return allProjects
      .filter(p => map[p.id])
      .map(p => ({ id: p.id, name: p.name, status: p.status, ...map[p.id] }))
      .sort((a, b) => b.invoiced - a.invoiced);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, allProjects, fxRates, baseCurrency]);

  return (
    <div style={{ padding: isMobileLayout ? "0.75rem" : "1rem", maxWidth: 1000, width: '100%', margin: "0 auto", boxSizing: 'border-box', overflowX: 'hidden' }}>
      {/* Page header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: isMobileLayout ? "stretch" : "center", flexDirection: isMobileLayout ? "column" : "row", marginBottom: "1.25rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "1.4rem" }}>Invoices</h2>
          <div style={{ fontSize: "0.82rem", color: "#aaa", marginTop: "0.15rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {invoices.filter(i => i.status !== "draft").length} invoice{invoices.filter(i => i.status !== "draft").length !== 1 ? "s" : ""}
            {stats.draftCount > 0 && (
              <span
                onClick={() => setActiveTab("Drafts")}
                style={{ background: "#f0f0f0", color: "#888", border: "1px solid #ccc", borderRadius: 6, padding: "0.05rem 0.45rem", fontSize: "0.72rem", fontWeight: 700, cursor: "pointer" }}
                title="View drafts"
              >
                {stats.draftCount} draft{stats.draftCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
        <button onClick={() => { setEditTarget(null); setShowForm(true); }} style={{ ...btnPrimary, width: isMobileLayout ? '100%' : 'auto', textAlign: 'center' }}>
          + New Invoice
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: stats.fxNote ? "0.4rem" : "1.25rem" }}>
        <StatCard isMobileLayout={isMobileLayout} label="Total invoiced" value={stats.total} sub={`${invoices.length} invoices`} />
        <StatCard isMobileLayout={isMobileLayout} label="Paid" value={stats.paid} color="#1e6645" sub={`${invoices.filter(i => i.status === "paid").length} invoices`} />
        <StatCard isMobileLayout={isMobileLayout} label="Pending" value={stats.pending} color="#7a4a10" sub={`${invoices.filter(i => i.status === "pending").length} invoices`} />
        <StatCard isMobileLayout={isMobileLayout} label="Overdue" value={stats.overdue} color="#A12710" sub={stats.overdueCount > 0 ? `${stats.overdueCount} overdue` : "All clear"} />
      </div>
      {stats.fxNote && (
        <div style={{ fontSize: "0.75rem", color: "#aaa", marginBottom: fxStale ? "0.25rem" : "1.25rem" }}>{stats.fxNote}</div>
      )}
      {fxStale && (
        <div style={{ fontSize: "0.75rem", color: "#b45309", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 6, padding: "0.3rem 0.6rem", marginBottom: "1.25rem", display: "inline-block" }}>
          Exchange rates may be outdated — live rates unavailable.
        </div>
      )}

      {/* Income breakdown by project */}
      {byProject.length > 0 && (
        <div style={{ marginBottom: "1.25rem", overflowX: "auto", borderRadius: 10, border: "1px solid #35454C", background: "#fff" }}>
          <table className="inv-projects-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#0C2D48", color: "#fff" }}>
                <th style={{ padding: "0.55rem 0.75rem", textAlign: "left", fontWeight: 700, fontSize: "0.78rem" }}>Project</th>
                <th style={{ padding: "0.55rem 0.75rem", textAlign: "right", fontWeight: 700, fontSize: "0.78rem" }}>Invoiced</th>
                <th style={{ padding: "0.55rem 0.75rem", textAlign: "right", fontWeight: 700, fontSize: "0.78rem" }}>Received</th>
                <th style={{ padding: "0.55rem 0.75rem", textAlign: "right", fontWeight: 700, fontSize: "0.78rem" }}>Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {byProject.map(p => (
                <tr
                  key={p.id}
                  style={{ background: "#fff", transition: "background 0.1s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                  onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                >
                  <td data-label="Project" style={{ padding: "0.55rem 0.75rem", fontSize: "0.87rem", borderBottom: "1px solid #f0f0f0" }}>
                    <span
                      onClick={onOpenProjectFinancials ? () => onOpenProjectFinancials(p.id) : undefined}
                      style={{
                        fontWeight: 700, color: "#2a6080",
                        cursor: onOpenProjectFinancials ? "pointer" : "default",
                        textDecoration: onOpenProjectFinancials ? "underline" : "none",
                        textDecorationColor: "#82B3CC",
                        textUnderlineOffset: "2px",
                      }}
                      title={onOpenProjectFinancials ? "View project financials" : undefined}
                    >
                      {p.name}
                    </span>
                    <span style={{ fontSize: "0.72rem", color: "#aaa", marginLeft: "0.4rem" }}>
                      ({p.count} invoice{p.count !== 1 ? "s" : ""})
                    </span>
                  </td>
                  <td data-label="Invoiced" style={{ padding: "0.55rem 0.75rem", textAlign: "right", fontSize: "0.87rem", fontWeight: 700, color: "#0C2D48", borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap" }}>
                    {fmtMoney(p.invoiced, baseSym)}
                  </td>
                  <td data-label="Received" style={{ padding: "0.55rem 0.75rem", textAlign: "right", fontSize: "0.87rem", fontWeight: 700, color: "#1e6645", borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap" }}>
                    {fmtMoney(p.received, baseSym)}
                  </td>
                  <td data-label="Outstanding" style={{ padding: "0.55rem 0.75rem", textAlign: "right", fontSize: "0.87rem", fontWeight: 700, color: p.invoiced - p.received > 0 ? "#7a4a10" : "#aaa", borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap" }}>
                    {fmtMoney(p.invoiced - p.received, baseSym)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Search */}
      <input
        type="text" placeholder={t('invoices.search_placeholder', 'Search by invoice number, contact or company…')}
        value={search} onChange={e => setSearch(e.target.value)}
        style={{ width: "100%", padding: "0.6rem 0.85rem", borderRadius: 8, border: "1px solid #ddd", fontSize: "0.95rem", marginBottom: "0.85rem", boxSizing: "border-box" }}
      />

      {/* Project filter */}
      {allProjects.length > 0 && (
        <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", marginBottom: "0.85rem", flexWrap: "wrap" }}>
          <select
            value={projectFilter}
            onChange={e => setProjectFilter(e.target.value)}
            style={{ padding: "0.5rem 0.7rem", borderRadius: 7, border: "1px solid #ddd", fontSize: "0.88rem", fontFamily: "Raleway, sans-serif", color: "#333", background: "#fff" }}
          >
            <option value="">All projects</option>
            <option value="none">— Unattached —</option>
            {allProjects.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
          </select>
          {projectFilter && (
            <button
              onClick={() => setProjectFilter("")}
              style={{ background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: "0.8rem", padding: 0, fontFamily: "Raleway, sans-serif" }}
            >
              Clear ✕
            </button>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: "0.35rem", flexWrap: "nowrap", marginBottom: "1rem", borderBottom: "2px solid #f0f0f0", paddingBottom: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {TABS.map(tab => {
          const count = tab === "All" ? invoices.length
            : tab === "Recurring" ? invoices.filter(i => !!i.recurring).length
            : tab === "Drafts" ? invoices.filter(i => i.status === "draft").length
              : invoices.filter(i => i.status === tab.toLowerCase()).length;
          return (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: "0.45rem 1rem", border: "none",
              borderBottom: activeTab === tab ? "2px solid #0C2D48" : "2px solid transparent",
              background: "transparent", fontWeight: activeTab === tab ? 700 : 500,
              fontSize: "0.88rem", color: activeTab === tab ? "#0C2D48" : "#888",
              cursor: "pointer", fontFamily: "Raleway, sans-serif", marginBottom: "-2px",
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}>
              {tab} <span style={{ fontSize: "0.75rem", opacity: 0.7 }}>({count})</span>
            </button>
          );
        })}
      </div>

      {/* Invoice list */}
      {filtered.length === 0 ? (
        <p style={{ textAlign: "center", color: "#aaa", padding: "2.5rem 0", fontSize: "0.9rem" }}>
          {invoices.length === 0 ? t('invoices.no_invoices') : t('empty.no_results')}
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {filtered.map(inv => {
            const st = STATUS[inv.status] || STATUS.pending;
            const totals = calcTotals(inv.lineItems);
            const sym = currencySymbol(inv.currency);
            const isOverdue = inv.status === "overdue";
            return (
              <div
                key={inv.id}
                onClick={() => setViewInvoice(inv)}
                style={{
                  display: "flex", alignItems: isMobileLayout ? "flex-start" : "center", gap: "1rem", flexWrap: "wrap", flexDirection: isMobileLayout ? "column" : "row",
                  padding: "0.85rem 1rem", border: `1px solid ${isOverdue ? "#f5a5a5" : "#35454C"}`,
                  borderLeft: `4px solid ${st.border}`, borderRadius: 10,
                  background: "#fff", cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  transition: "box-shadow 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)"}
              >
                <div style={{ minWidth: isMobileLayout ? 0 : 110, width: isMobileLayout ? '100%' : 'auto', fontWeight: 700, fontSize: "0.9rem", color: "#0C2D48" }}>
                  {inv.number}
                  {inv.approvalStatus === "pending" && <span style={{ fontSize: "0.62rem", marginLeft: "0.35rem", background: "#fff8ee", color: "#7a4a10", border: "1px solid #FBC490", borderRadius: 5, padding: "0 0.3rem", fontWeight: 700 }} title="Awaiting approval">⏳ Pending</span>}
                  {inv.approvalStatus === "rejected" && <span style={{ fontSize: "0.62rem", marginLeft: "0.35rem", background: "#fde8e8", color: "#A12710", border: "1px solid #f5a5a5", borderRadius: 5, padding: "0 0.3rem", fontWeight: 700 }} title={inv.rejectionReason ? `Rejected: ${inv.rejectionReason}` : "Rejected"}>✕ Rejected</span>}
                  {inv.recurring && <span style={{ fontSize: "0.65rem", marginLeft: "0.3rem", color: "#82B3CC" }}>🔁</span>}
                  {inv.linkedTaskId && <span style={{ fontSize: "0.65rem", marginLeft: "0.3rem", color: "#4a80cc" }} title="Chase task active">📋</span>}
                  {(inv.reminders?.filter(r => r.status === "pending").length > 0) && (
                    <span style={{ fontSize: "0.65rem", marginLeft: "0.3rem", color: "#b07800" }} title={`${inv.reminders.filter(r => r.status === "pending").length} reminder(s) scheduled`}>
                      🔔{inv.reminders.filter(r => r.status === "pending").length}
                    </span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: isMobileLayout ? 0 : 140, width: isMobileLayout ? '100%' : 'auto' }}>
                  <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{inv.contactName}</div>
                  {inv.contactCompany && <div style={{ fontSize: "0.78rem", color: "#888" }}>{inv.contactCompany}</div>}
                </div>
                <div
                  style={{
                    fontSize: "0.78rem", minWidth: isMobileLayout ? 0 : 100, width: isMobileLayout ? '100%' : 'auto',
                    color: inv.project_id ? "#2a6080" : "#ccc",
                    fontWeight: inv.project_id ? 600 : 400,
                    cursor: onOpenProjectFinancials && inv.project_id ? "pointer" : "default",
                    textDecoration: onOpenProjectFinancials && inv.project_id ? "underline" : "none",
                    textDecorationColor: "#82B3CC",
                    textUnderlineOffset: "2px",
                  }}
                  onClick={onOpenProjectFinancials && inv.project_id ? (e) => { e.stopPropagation(); onOpenProjectFinancials(inv.project_id); } : undefined}
                  title={onOpenProjectFinancials && inv.project_id ? "View project financials" : undefined}
                >
                  {inv.project_id ? (projectsLookup[inv.project_id] || "—") : "—"}
                </div>
                <div style={{ fontSize: "0.8rem", color: "#aaa", minWidth: isMobileLayout ? 0 : 80, width: isMobileLayout ? '100%' : 'auto' }}>
                  Due {fmtDate(inv.dueDate)}
                </div>
                <div style={{ fontWeight: 800, fontSize: "1rem", color: "#0C2D48", minWidth: isMobileLayout ? 0 : 80, width: isMobileLayout ? '100%' : 'auto', textAlign: isMobileLayout ? 'left' : "right" }}>
                  {fmtMoney(totals.total, sym)}
                </div>
                <span style={{ background: st.bg, color: st.text, border: `1px solid ${st.border}`, borderRadius: 8, padding: "0.15rem 0.6rem", fontSize: "0.72rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                  {st.label}
                </span>
                <button
                  onClick={e => { e.stopPropagation(); printInvoice(inv); }}
                  style={{ padding: "0.25rem 0.65rem", background: "#f0f4f8", color: "#0C2D48", border: "1px solid #c5d4e0", borderRadius: 6, fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                  title="Download PDF"
                >
                  ⬇ PDF
                </button>
                {inv.status !== "paid" && inv.status !== "draft" && (
                  <button
                    onClick={e => { e.stopPropagation(); markPaid(inv.id); }}
                    style={{ padding: "0.25rem 0.65rem", background: "#eaf6ef", color: "#1e6645", border: "1px solid #6abf85", borderRadius: 6, fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}
                  >
                    Mark paid
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <InvoiceFormModal
          invoice={editTarget}
          allInvoices={invoices}
          onSave={saveInvoice}
          onClose={() => { setShowForm(false); setEditTarget(null); setPreselectedProjectId(null); setPreselectedTimeEntries(null); }}
          preselectedProjectId={preselectedProjectId}
          preselectedTimeEntries={preselectedTimeEntries}
        />
      )}

      {viewInvoice && (
        <InvoiceDetailModal
          invoice={viewInvoice}
          projects={allProjects}
          paymentDetails={paymentDetails}
          bookingUsername={bookingUsername}
          gated={invoiceGated}
          canApprove={canApproveInvoices}
          onSubmitForApproval={submitInvoiceForApproval}
          onApprove={approveInvoice}
          onReject={rejectInvoice}
          onClose={() => setViewInvoice(null)}
          onEdit={inv => { setEditTarget(inv); setShowForm(true); setViewInvoice(null); }}
          onMarkPaid={markPaid}
          onVoid={voidInvoice}
          onUnvoid={unvoidInvoice}
          onDelete={(id) => { setViewInvoice(null); setDeleteConfirm({ id, number: invoices.find(i => i.id === id)?.number || String(id) }); }}
          onUpdateReminders={updateReminders}
          onUpdateProject={updateProject}
        />
      )}

      {toast && (
        <div style={{ position: "fixed", bottom: "1.5rem", left: "50%", transform: "translateX(-50%)", background: "#0C2D48", color: "#fff", padding: "0.7rem 1.4rem", borderRadius: 8, fontWeight: 600, fontSize: "0.9rem", zIndex: 2000, boxShadow: "0 4px 16px rgba(0,0,0,0.2)", whiteSpace: "nowrap" }}>
          ✓ {toast}
        </div>
      )}

      <ConfirmDeleteModal
        open={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteInvoice(deleteConfirm.id)}
        itemType="invoice"
        itemName={deleteConfirm?.number || ''}
        warningText="This will permanently delete this invoice."
      />
    </div>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────
const inp = {
  display: "block", width: "100%", marginTop: "0.3rem",
  padding: "0.5rem 0.7rem", borderRadius: 6, border: "1px solid #ddd",
  fontSize: "0.92rem", boxSizing: "border-box", fontFamily: "Raleway, sans-serif",
};
const lbl = { display: "block", fontSize: "0.8rem", fontWeight: 600, color: "#555" };
const cellInput = { padding: "0.3rem 0.4rem", border: "1px solid #e0e0e0", borderRadius: 5, fontSize: "0.82rem", width: "100%", boxSizing: "border-box", fontFamily: "Raleway, sans-serif" };
const btnPrimary = { padding: "0.55rem 1.2rem", background: "#E4E69B", border: "none", borderRadius: 7, fontWeight: 700, fontSize: "0.9rem", cursor: "pointer", fontFamily: "Raleway, sans-serif", color: "#0C2D48" };
const btnSecondary = { padding: "0.55rem 1.2rem", background: "#f3f3f3", border: "1px solid #ddd", borderRadius: 7, fontWeight: 600, fontSize: "0.9rem", cursor: "pointer", fontFamily: "Raleway, sans-serif", color: "#333" };
const btnDanger = { padding: "0.55rem 1.2rem", background: "#E96A6A", border: "none", borderRadius: 7, fontWeight: 600, fontSize: "0.9rem", cursor: "pointer", fontFamily: "Raleway, sans-serif", color: "#fff" };
const btnGhost = { padding: "0.55rem 1.2rem", background: "transparent", border: "1px solid #ddd", borderRadius: 7, fontWeight: 600, fontSize: "0.9rem", cursor: "pointer", fontFamily: "Raleway, sans-serif", color: "#333" };

export default InvoicingDashboard;
